import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BrowserUpdatedIcon from '@mui/icons-material/BrowserUpdated';
import { Broker } from "./interface";
import Form from "react-bootstrap/Form";
import "../../Components/SharedComponents/InputBox.scss";
import {
  MaterialReactTable,
  // createRow,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  type MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_VisibilityState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type User, fakeData, usStates } from "./makeData";
import { citiesList, usStateList } from "./makeData";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import Table from "react-bootstrap/Table";
import { Col, Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import { StyledTableFirstSecond } from "../Book/TableBook";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";
import { CommonToolbarInternalActions } from "../../Components/SharedComponents/CommonTableToolbarActions";
import { MRT_COLUMN_FILTERS_CLEARING_ACCOUNT, MRT_COLUMN_VISIBILITY_CLEARING_ACCOUNT, MRT_DENSITY_CLEARING_ACCOUNT, MRT_GLOBAL_FILTERS_CLEARING_ACCOUNT, MRT_SHOW_COLUMN_FILTERS_CLEARING_ACCOUNT, MRT_SHOW_GLOBAL_FILTERS_CLEARING_ACCOUNT, MRT_SORTING_CLEARING_ACCOUNT } from "../../Constants/TableFilter.constants";
const Example = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  // Add touched state to track which fields have been interacted with
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const isFirstRender = useRef(true);
  const [filterModes, setFilterModes] = useState<
    { mode: string; id: string; value: unknown }[]
  >([]);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(
    undefined
  );
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [brokerName, setBrokerName] = useState<string>("");
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15, //set different default page size by initializing the state here
  });

  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const handleFileUploadSubmit = async (file: File, fileName: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", fileName);

    try {
      const response = await axiosInstance.post("", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return {
        success: true,
        message: response.data.message || "File uploaded successfully!",
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload file",
      };
    }
  };

  // Validation helper functions
  const validateBrokerName = (value: string): string => {
    if (!value || !value.trim()) {
      return "Clearing Account name is required";
    }
    if (value.trim().length < 2) {
      return "Clearing Account name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "Clearing Account name must not exceed 50 characters";
    }
    return "";
  };

  // Handle field blur for validation
  const handleFieldBlur = (fieldName: string, value: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    let error = "";
    if (fieldName === "brokerName") {
      error = validateBrokerName(value);
    }

    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle field focus to clear errors
  const handleFieldFocus = (fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  };

  // Check if field should show error (only if touched and has error)
  const shouldShowError = (fieldName: string): boolean => {
    return touchedFields[fieldName] && !!validationErrors[fieldName];
  };

  useEffect(() => {
    //do something when the pagination state changes
  }, [pagination]);

  useEffect(() => {
    const columnFilters = sessionStorage.getItem(MRT_COLUMN_FILTERS_CLEARING_ACCOUNT);
    const columnVisibility = sessionStorage.getItem(
      MRT_COLUMN_VISIBILITY_CLEARING_ACCOUNT
    );
    const density = sessionStorage.getItem(MRT_DENSITY_CLEARING_ACCOUNT);
    const globalFilter = sessionStorage.getItem(MRT_GLOBAL_FILTERS_CLEARING_ACCOUNT);
    const showGlobalFilter = sessionStorage.getItem(
      MRT_SHOW_GLOBAL_FILTERS_CLEARING_ACCOUNT
    );
    const showColumnFilters = sessionStorage.getItem(
      MRT_SHOW_COLUMN_FILTERS_CLEARING_ACCOUNT
    );
    const sorting = sessionStorage.getItem(MRT_SORTING_CLEARING_ACCOUNT);

    if (columnFilters) {
      setColumnFilters(JSON.parse(columnFilters));
    }
    if (columnVisibility) {
      setColumnVisibility(JSON.parse(columnVisibility));
    }
    if (density) {
      setDensity(JSON.parse(density));
    }
    if (globalFilter) {
      setGlobalFilter(JSON.parse(globalFilter) || undefined);
    }
    if (showGlobalFilter) {
      setShowGlobalFilter(JSON.parse(showGlobalFilter));
    }
    if (showColumnFilters) {
      setShowColumnFilters(JSON.parse(showColumnFilters));
    }
    if (sorting) {
      setSorting(JSON.parse(sorting));
    }
    isFirstRender.current = false;
  }, []);

  //save states to local storage
  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_COLUMN_FILTERS_CLEARING_ACCOUNT,
      JSON.stringify(columnFilters)
    );
  }, [columnFilters]);
  //save states to local storage
  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_COLUMN_FILTERS_CLEARING_ACCOUNT,
      JSON.stringify(columnFilters)
    );

    console.log("columnFilters", "columnFilters---------------", columnFilters);
  }, [columnFilters]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_COLUMN_VISIBILITY_CLEARING_ACCOUNT,
      JSON.stringify(columnVisibility)
    );
  }, [columnVisibility]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(MRT_DENSITY_CLEARING_ACCOUNT, JSON.stringify(density));
  }, [density]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_GLOBAL_FILTERS_CLEARING_ACCOUNT,
      JSON.stringify(globalFilter ?? "")
    );
  }, [globalFilter]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_SHOW_GLOBAL_FILTERS_CLEARING_ACCOUNT,
      JSON.stringify(showGlobalFilter)
    );
  }, [showGlobalFilter]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(
      MRT_SHOW_COLUMN_FILTERS_CLEARING_ACCOUNT,
      JSON.stringify(showColumnFilters)
    );
  }, [showColumnFilters]);

  useEffect(() => {
    if (isFirstRender.current) return;
    sessionStorage.setItem(MRT_SORTING_CLEARING_ACCOUNT, JSON.stringify(sorting));
  }, [sorting]);

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      // {
      //   accessorKey: "id",
      //   header: "Id",
      //   enableEditing: false,

      //   // minSize: 40,
      //   // maxSize: 100,
      // },

      {
        accessorKey: "name",
        header: "Clearing account",
        enableEditing: true,

        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => {
            table.setEditingRow(row);
          },
          sx: {
            height: "10px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(44, 4, 4, 0.05)",
            },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              transition: "background-color 0.3s ease",
            }),
          },
        }),

        muiEditTextFieldProps: {
          name: "name", // Ensure the correct field is targeted
          required: true,
          error: shouldShowError("name"),
          helperText: touchedFields["name"] ? validationErrors?.name : "",
          onFocus: () => handleFieldFocus("name"),
          onBlur: (e) => handleFieldBlur("name", e.target.value),
        },
      },
    ],
    [validationErrors, touchedFields]
  );

  //call CREATE hook
  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  //call READ hook
  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetData(filterModes, pagination, setTotalCount, sorting);
  //call UPDATE hook

  const { mutateAsync: updateUser, isPending: isUpdatingUser } =
    useUpdateUser();
  //call DELETE hook
  const { mutateAsync: deleteUser, isPending: isDeletingUser } =
    useDeleteUser();

  // In your createBroker function, replace the existing function with this:
  const createBroker = async (broker: { id: string; name: string }) => {
    if (!broker.name) {
      toast.error("Clearing Account name is Required!");
      return;
    }

    try {
      // Make an API call to add the broker
      await axiosInstance.post(
        `${backendbaseUrl}/paper-app/clearing-accounts/`,
        broker
      );

      // Invalidate the query with the correct parameters to refetch the updated data
      queryClient.invalidateQueries({
        queryKey: ["users", filterModes, pagination, sorting],
      });

      // Show a success message
      toast.success("Clearing Account added successfully!");
    } catch (error) {
      console.error("Error adding Clearing Account:", error);
      toast.error("Failed to add Clearing Account. Please try again.");
    }
  };
  // create by submitting the form
  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Mark field as touched for validation
    setTouchedFields((prev) => ({ ...prev, brokerName: true }));
    // Validate the field
    const nameError = validateBrokerName(brokerName);
    if (nameError) {
      setValidationErrors((prev) => ({ ...prev, brokerName: nameError }));
      return;
    }
    const broker = {
      id: Date.now().toString(), // Generate a unique ID
      name: brokerName.trim(), // Use the broker name from the input field
    };

    await createBroker(broker);

    // Reset the form and validation states
    setBrokerName("");
    setTouchedFields({});
    setValidationErrors({});
  };

  // Also update your handleCreateUser function:
  const handleCreateUser: MRT_TableOptions<User>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateUser(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setValidationErrors({});

    const broker = {
      id: Date.now().toString(),
      name: values.name.trim(),
    };

    await createBroker(broker);
    table.setCreatingRow(null);
  };

  //UPDATE action
  const handleeditsave: MRT_TableOptions<User>["onEditingRowSave"] = async ({
    values,
    table,
    row, // Add row parameter to access full row data
  }) => {
    // Log the detailed values and table object
    console.log("Saving User - Full Row Original Data:", row.original);
    console.log("Saving User - Edited Values:", values);

    // Merge the edited values with the original row data
    const updatedUser = {
      ...row.original, // Spread the original row data first
      ...values, // Overlay the edited values on top
    };

    console.log("Saving User - Merged User Data:", updatedUser);

    // Validate user data
    const newValidationErrors = validateUser(updatedUser);

    // Check for validation errors
    if (Object.values(newValidationErrors).some((error) => error)) {
      console.log("Validation Errors:", newValidationErrors);
      setValidationErrors(newValidationErrors);
      return;
    }

    // Clear previous validation errors
    setValidationErrors({});

    // Trigger user update mutation
    try {
      await updateUser(updatedUser); // Pass the full, merged user object

      // Exit editing mode
      table.setEditingRow(null);
    } catch (error) {
      console.error("User Update Error:", error);
    }
  };
const handleBulkDelete = async (selectedRows: MRT_Row<User>[]) => {
  const selectedIds = selectedRows.map((row) => row.original.id);

  try {
    // Make API call to bulk delete
      const response = await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/clearingaccounts/export-delete/`,
        {
          data: { ids: selectedIds },
          headers: { "Content-Type": "application/json" },
        }
      );

      queryClient.invalidateQueries({
        queryKey: ["users", filterModes, pagination, sorting],
      });

      const { message, warning } = response.data;
      if (warning) {
        toast.error(warning);
      } else {
        toast.success(message || "Selected Clearing Accounts deleted successfully!");
      }
    } catch (error: any) {
      console.error("Error deleting selected Clearing Accounts:", error);
      const errMsg = error.response?.data?.message || "Failed to delete selected Clearing Accounts. Please try again.";
      toast.error(errMsg);
    }
  };
  //DELETE action
 const openDeleteConfirmModal = (
  rowOrRows: MRT_Row<User> | MRT_Row<User>[],
  isBulk: boolean = false
) => {
  const message = isBulk
    ? `Are you sure you want to delete ${
        Array.isArray(rowOrRows) ? rowOrRows.length : 1
      } selected clearing accounts?`
    : "Are you sure you want to delete this clearing account?";

  showDeleteConfirmModal({
    title: message,
    content: `Deleting ${isBulk ? "selected accounts" : "account"}...`,
    onOk: () => {
      if (isBulk && Array.isArray(rowOrRows)) {
        // Handle bulk deletion
        handleBulkDelete(rowOrRows).catch((error) => {
          console.error("Bulk delete failed:", error);
        });
      } else if (!isBulk && !Array.isArray(rowOrRows)) {
        // Handle single deletion
        deleteUser(rowOrRows.original.id)
          .then(() => {
            toast.success("Clearing Account deleted successfully!");
          })
          .catch((error) => {
            toast.error("Failed to delete Clearing Account. Please try again.");
          });
      }
    },
  });
};

  const handleCopyRow = (row: any) => {
    if (!row) return;
    const rowData = row.original; // Get the row data
    let textToCopy = JSON.stringify(rowData, null, 2); // Convert to formatted JSON
     if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(textToCopy).catch((err) => {
      console.error("Clipboard write failed:", err);
    });
  } else {
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }// Copy to clipboard

    let jsonData;

    try {
      jsonData = JSON.parse(textToCopy);
      setBrokerName(jsonData.name);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => {
        setInputBackgroundColour(""); // Then set it again
      }, 2400);
    } catch (error) {
      console.error("Invalid JSON. Pasted as a string:", textToCopy);
      jsonData = { rawText: textToCopy }; // Fallback if not valid JSON
    }
  };

  interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  // Function to handle right-click and open the menu
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  // Function to handle menu close
  const handleClose = () => {
    setContextMenu(null);
  };

  // Function to handle Paste action
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      let jsonData;

      try {
        jsonData = JSON.parse(text);
        setBrokerName(jsonData.name);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success("copied and pasted successfully");
        setInputBackgroundColour("#83bf6e26");
        setTimeout(() => {
          setInputBackgroundColour(""); // Then set it again
        }, 2400);
      } catch (error) {
        console.error("Invalid JSON. Pasted as a string:", text);
        jsonData = { rawText: text }; // Fallback if not valid JSON
      }

      // Here, you can use jsonData further as needed
    } catch (err) {
      console.error("Failed to read clipboard contents:", err);
    }

    handleClose();
  };

  // Function to handle Mark action
  const handleResizing = () => {
    setShowRowResizing(!showRowResizing);
    handleClose();
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log(rowData);
  };

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        "/paper-app/clearing-accounts/csv/download",
        {
          responseType: "blob", // Ensure Axios handles binary data properly
        }
      );

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = "persons.csv"; // Set the file name
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const table = useMaterialReactTable({
    columns,

    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    // enableRowNumbers: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    onPaginationChange: setPagination,
    // onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: true,
    // positionToolbarAlertBanner: "bottom",
    // layoutMode: "semantic",
    // enableColumnResizing: showRowResizing,
    enableColumnResizing: true, // Already set
    // columnResizeMode: "onEnd", // Ensures precise resizing
    // defaultColumn: {
    //   minSize: 50, // Smallest practical width
    //   enableResizing: true,
    // },
    enableFilterMatchHighlighting: true,
    
  enableColumnFilters: true,
  enableColumnFilterModes: true,

    rowCount: totalCount,
    // manualFiltering: true,
    data: data?.results || [],
    enableDensityToggle: false,
    createDisplayMode: "row", // ('modal', and 'custom' are also available)
    editDisplayMode: "row", // ('modal', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,

    // Top toolbar customization
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),

    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();

          const editingRow = table.getState().editingRow;
          if (editingRow) {
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = {
                ...editingRow.original,
                ...editingRow._valuesCache,
              };
              console.log("Saving:", newedit);

              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: newedit,
              });
            }
          }
        }
      },
    }),

    enableColumnOrdering: true, // ✅ Enables column drag-and-drop
    enableRowOrdering: false, // ✅ Enables row drag-and-drop
    getRowId: (row) => row.id,
    // enableColumnFilterModes: true, //enable changing filter mode for all columns unless explicitly disabled in a column def
    // initialState: { showColumnFilters: true }, //show filters by default
    filterFns: {
      customFilterFn: (row, id, filterValue) => {
        return row.getValue(id) === filterValue;
      },
    },

    // defaultDisplayColumn: {
    //   enableResizing: true, //turn on some features that are usually off for all display columns
    // },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 120, //set custom width
        muiTableHeadCellProps: {
          // align: "center", //change head cell props
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: true,
        enableColumnOrdering: true, //turn on some features that are usually off
        // enableResizing: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
        // size: 50,
      },
    },
    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        className="table-download__wrapper"
        sx={{
          display: "flex",
          gap: "6px",
          padding: "0px",
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
          Upload File
        </Button>
        <Button
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={() => handleDataDownload()}
          startIcon={<BrowserUpdatedIcon />}
        >
          Export All Data
        </Button>
        {/* <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          //export all rows, including from the next page, (still respects filtering and sorting)

          startIcon={<FileDownloadIcon />}
        >
          Export All Rows
        </Button>
        <Button
          disabled={table.getRowModel().rows.length === 0}
          //export all rows as seen on the screen (respects pagination, sorting, filtering, etc.)

          startIcon={<FileDownloadIcon />}
        >
          Export Page Rows
        </Button> */}
        <Button
          disabled={
            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
          }
          //only export selected rows
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
      <Button
        style={{ color: "red" }}
        onClick={() =>
          openDeleteConfirmModal(table.getSelectedRowModel().rows, true)
        }
        startIcon={<DeleteIcon />}
      >
        Delete Selected
      </Button>
    )}
      </Box>
    ),

    // muiTableProps: {
    //   sx: {
    //     border: '1px solid rgba(81, 81, 81, .5)',
    //     caption: {
    //       captionSide: 'top',
    //     },
    //   },
    // },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
      },
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "0",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
    } as any,

    muiToolbarAlertBannerProps: isLoadingUsersError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateUser,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleeditsave,

    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            {" "}
            {/* ✅ Pass row as argument */}
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    state: {
      columnVisibility,
      sorting,
      columnFilters,
      pagination,
      isLoading: isLoadingUsers,
      isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
      showColumnFilters: showColumnFilters,
    },
  });

  const transformedFilters = columnFilters.map((filter) => ({
    ...filter,
    mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
  }));

  useEffect(() => {
    const transformedFilters = columnFilters.map((filter) => ({
      ...filter,
      mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
    }));

    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  // const someEventHandler = (event: any) => {
  //   console.info("rows :", table.getRowModel().rows); //example - get access to all page rows in the table
  //   console.info("row model :", table.getSelectedRowModel()); //example - get access to all selected rows in the table
  //   console.info("row model :", table.getState().sorting); //example - get access to the current sorting state without having to manage it yourself
  // };
  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Clearing account
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div
                        className={`flex formWrapper ${
                          inputBackgroundColour !== "" ? "inputboxcustom" : ""
                        }`}
                        style={{ backgroundColor: inputBackgroundColour }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            // backgroundColor: "#f8f9fa",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            className="form_default"
                            component="form" // This is now the only form element
                            onSubmit={formCreate}
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              gap: 0.5,
                              "& > *": {
                                // flex: "1 1 0px",
                                minWidth: {
                                  xs: "100%",
                                  sm: "48%",
                                  md: "23%",
                                  lg: "15%",
                                  xl: "7.5%",
                                },
                              },
                            }}
                          >
                            {/* <TextField
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": {
                                  fontSize: "0.7rem",
                                  height: "32px",
                                  fontWeight: "bold",
                                  marginBottom: "0.5rem",
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.9rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": {
                                    // transform: "translate(14px, -6px) scale(0.75)",
                                    transform:
                                      "translate(14px, -8px) scale(0.74)",
                                    backgroundColor: "white",
                                    paddingRight: "0.4rem",
                                    fontWeight: "bold",
                                    color: "black",
                                    // letterSpacing: "1px",
                                    letterSpacing: "normal",
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  // borderColor: "#C0C0C0",
                                  borderColor: "#b9baeb85",
                                  borderWidth: "2px",
                                },
                                "& .MuiInputBase-input::placeholder": {
                                  color: "#999",
                                  opacity: 1,
                                },
                                "& .MuiInputBase-input": {
                                  textTransform: "capitalize",
                                },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                              id="outlined-basic"
                              label="Clearing account name"
                              variant="outlined"
                              size="small"
                              value={brokerName}
                              onChange={(e) => setBrokerName(e.target.value)}
                              slotProps={{
                                inputLabel: {
                                  shrink: true, // Explicitly prevent shrink
                                },
                              }}

                              error={!!validationErrors?.brokerName}
                              // helperText={validationErrors?.brokerName}
                              onBlur={() =>
                                handleFieldBlur("brokerName", brokerName)
                              }

                            /> */}
                            <CompactTextField
                              label="Clearing account name"
                              value={brokerName}
                              onChange={(value) => setBrokerName(value)}
                              name="brokerName"
                              required
                              error={!!validationErrors?.brokerName}
                              helperText={
                                touchedFields["brokerName"]
                                  ? validationErrors?.brokerName
                                  : ""
                              }
                              validateOnBlur={(value) => {
                                const error = validateBrokerName(value);
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  brokerName: error,
                                }));
                                return error;
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />
                            <Button
                              variant="contained"
                              type="submit"
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "100px" },
                                maxWidth: { sm: "150px" },
                                backgroundColor: "#00bcd4",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": {
                                  backgroundColor: "#008c9e",
                                },
                              }}
                            >
                              Add
                            </Button>
                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <div
                className="table-ad__wrapper"
                onContextMenu={handleContextMenu}
              >
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />

                  {/* Context Menu */}
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                    }
                  >
                    {/* <MenuItem onClick={handlePaste}>Paste</MenuItem> */}
                    <MenuItem onClick={handleResizing}>
                      {showRowResizing ? "Disable Resizing" : "enable Resizing"}
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

//CREATE hook (post new user to api)
function useCreateUser() {
  const queryClient = useQueryClient();
  console.log("create user");
  return useMutation({
    mutationFn: async (user: User) => {
      //send api update request here
      await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
      return Promise.resolve();
    },
    //client side optimistic update
    onMutate: (newUserInfo: User) => {
      queryClient.setQueryData(
        ["users"],
        (prevUsers: any) =>
          [
            ...prevUsers,
            {
              ...newUserInfo,
              id: (Math.random() + 1).toString(36).substring(7),
            },
          ] as User[]
      );
    },
    // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
  });
}

//READ hook (get users from api)
function useGetData(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: any,
  sorting: any
) {
  return useQuery<{ results: User[]; totalCount: number }>({
    // ✅ Correct return type
    queryKey: ["users", filterModes, pagination, sorting], // Ensure proper re-fetching
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/clearing-accounts/?page=${
        pagination.pageIndex + 1
      }&page_size=${pagination.pageSize}`;

      if (filterModes.length > 0) {
        console.log("filterModes---,", filterModes);
        const queryParams = filterModes
          .map((e) => `fieldName_${e.id}=${String(e.value)}_${e.mode}`)
          .join("&");

        url += `&${queryParams}`;
      }
      console.log("sortparams:", sorting);
      if (sorting.length > 0) {
        const sortParams = `sortField_${sorting[0].id}=${sorting[0].desc}_sorting`;
        url += `&${sortParams}`;

        console.log("yesss");
      } else {
        console.log("sortParams");
      }

      const response = await axiosInstance.get(url);
      setTotalCount(response.data.count);
      return {
        results: response.data.results as User[],
        totalCount: response.data.count as number,
      };
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: User) => {
      // Comprehensive logging
      console.group("🔄 User Update Mutation");
      console.log("Input User Object:", user);

      // Validate basic requirements
      if (!user || !user.id) {
        console.error("❌ Invalid user object:", user);
        throw new Error("User object is invalid or missing ID");
      }

      // Prepare payload - now using the full user object
      const payload = user;

      console.log("📦 Prepared Payload:", payload);

      console.groupEnd();
      try {
        const { data } = await axiosInstance.put(
          `/paper-app/clearing-accounts/${payload.id}/`,
          user
        );
        console.log("✅ API Response:", data);
        console.groupEnd();
        return data;
      } catch (error) {
        console.error("❌ API Request Failed:", error);
        throw error;
      }
    },

    // Rest of the mutation remains the same
    onMutate: async (newUserInfo: User) => {
      console.log("🔮 Optimistic Update - New User Info:", newUserInfo);

      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ["users"] });

      // Snapshot previous data
      const previousUsers = queryClient.getQueryData(["users"]);

      // Optimistically update cache
      queryClient.setQueryData(["users"], (oldData: any) =>
        oldData?.map((prevUser: User) =>
          prevUser.id === newUserInfo.id ? newUserInfo : prevUser
        )
      );

      return { previousUsers };
    },

    onError: (err, newUser, context) => {
      console.error("❌ Update Failed:", err);

      // Rollback optimistic update
      queryClient.setQueryData(["users"], context?.previousUsers);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

//DELETE hook (delete user in api)
function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      //send api update request here
      // await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
      try {
        await axiosInstance.delete(
          `${backendbaseUrl}/paper-app/clearing-accounts/${userId}/`
        );
        console.log("userId", userId);
      } catch (error) {
        console.error("Error deleting user:", error);
        throw error;
      }

      return Promise.resolve();
    },
    //client side optimistic update
    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousUsers = queryClient.getQueryData(["users"]);

      queryClient.setQueryData(["users"], (prevUsers: any) =>
        prevUsers?.filter((user: User) => user.id !== userId)
      );

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      console.error("❌ Delete Failed:", err);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }), //refetch users after mutation, disabled for demo
  });
}

//react query setup in App.tsx
const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      {/* <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction />
      </Suspense> */}
    </QueryClientProvider>
  );
}

const validateRequired = (value: string) => !!value.length;
const validateEmail = (email: string) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

function validateUser(user: User) {
  return {
    // brokerName: !validateRequired(user.name)
    //   ? "First Name is Required"
    //   : "",
    // name: !validateRequired(user.name) ? "Last Name is Required" : "",
    // email: !validateEmail(user.email) ? "Incorrect Email Format" : "",
  };
}
